import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { X } from 'lucide-react';

const UserForm = ({ user, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    role: user?.role || 'client',
    is_active: user?.is_active !== undefined ? user?.is_active : true,
    avatar_url: user?.avatar_url || ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email est requis';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Nom complet est requis';
    }

    if (formData?.phone && !/^\+?[\d\s-()]+$/?.test(formData?.phone)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);

      if (user?.id) {
        // Update existing user
        const { error } = await supabase?.from('user_profiles')?.update({
            ...formData,
            updated_at: new Date()?.toISOString()
          })?.eq('id', user?.id);

        if (error) throw error;
      } else {
        // Create new user - first create auth user then profile
        const { data: authData, error: authError } = await supabase?.auth?.signUp({
          email: formData?.email,
          password: 'TempPassword123!', // Temporary password
          options: {
            data: {
              full_name: formData?.full_name,
              role: formData?.role
            }
          }
        });

        if (authError) throw authError;

        if (authData?.user) {
          // Update the user profile with additional data
          const { error: profileError } = await supabase?.from('user_profiles')?.update({
              phone: formData?.phone,
              is_active: formData?.is_active,
              avatar_url: formData?.avatar_url
            })?.eq('id', authData?.user?.id);

          if (profileError) throw profileError;
        }
      }

      onSave?.();
      alert(user ? 'Utilisateur mis à jour avec succès' : 'Utilisateur créé avec succès');
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Erreur lors de la sauvegarde: ' + (error?.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-90vh overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold">
            {user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData?.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e?.target?.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors?.email ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={!!user} // Don't allow email change for existing users
            />
            {errors?.email && <p className="text-red-500 text-sm mt-1">{errors?.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom complet *
            </label>
            <input
              type="text"
              value={formData?.full_name}
              onChange={(e) => setFormData(prev => ({ ...prev, full_name: e?.target?.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors?.full_name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors?.full_name && <p className="text-red-500 text-sm mt-1">{errors?.full_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData?.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e?.target?.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors?.phone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="+225 XX XX XX XX XX"
            />
            {errors?.phone && <p className="text-red-500 text-sm mt-1">{errors?.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rôle
            </label>
            <select
              value={formData?.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="client">Client</option>
              <option value="agent">Agent</option>
              <option value="notary">Notaire</option>
              <option value="admin">Administrateur</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL de l'avatar
            </label>
            <input
              type="url"
              value={formData?.avatar_url}
              onChange={(e) => setFormData(prev => ({ ...prev, avatar_url: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com/avatar.jpg"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData?.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e?.target?.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Utilisateur actif
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Sauvegarde...' : (user ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;